import { Capacitor } from "@capacitor/core";
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
  type PermissionStatus,
} from "@capacitor/push-notifications";
import { notificationsService } from "@/services/notifications.service";
import { StashtBadge } from "@/plugins/StashtBadge";

const STORAGE_KEYS = {
  DEVICE_ID: "stasht_device_id",
  PUSH_TOKEN: "stasht_push_token",
} as const;

const DEEP_LINK_EVENT = "stasht:deep-link";
const PENDING_DEEP_LINK_KEY = "stasht_pending_deep_link";
const PENDING_PUSH_READ_KEY = "stasht_pending_push_read";

const PLATFORM_MAP = {
  ios: "ios",
  android: "android",
} as const;

let listenersAttached = false;

const getStoredValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setStoredValue = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures
  }
};

const clearStoredValue = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures
  }
};

const getOrCreateDeviceId = (): string => {
  const existing = getStoredValue(STORAGE_KEYS.DEVICE_ID);
  if (existing) {
    return existing;
  }
  const nextId = crypto.randomUUID();
  setStoredValue(STORAGE_KEYS.DEVICE_ID, nextId);
  return nextId;
};

const storePushToken = (token: string) => {
  setStoredValue(STORAGE_KEYS.PUSH_TOKEN, token);
};

const loadPushToken = (): string | null => getStoredValue(STORAGE_KEYS.PUSH_TOKEN);

const storePendingDeepLink = (link: string) => {
  setStoredValue(PENDING_DEEP_LINK_KEY, link);
};

export const consumePendingDeepLink = (): string | null => {
  const pending = getStoredValue(PENDING_DEEP_LINK_KEY);
  if (pending) {
    clearStoredValue(PENDING_DEEP_LINK_KEY);
  }
  return pending;
};

const dispatchDeepLink = (link: string) => {
  storePendingDeepLink(link);
  try {
    window.dispatchEvent(new CustomEvent(DEEP_LINK_EVENT, { detail: link }));
  } catch {
    // Ignore dispatch failures
  }
};


const getPlatform = (): "ios" | "android" | null => {
  const platform = Capacitor.getPlatform();
  return PLATFORM_MAP[platform as keyof typeof PLATFORM_MAP] ?? null;
};

const sendTokenUpdate = async (token: string, enabled: boolean) => {
  const platform = getPlatform();
  if (!platform) {
    return;
  }
  try {
    await notificationsService.registerDeviceToken({
      device_id: getOrCreateDeviceId(),
      token,
      platform,
      enabled,
    });
  } catch (error) {
    console.warn("Failed to register device token:", error);
  }
};

const attachListeners = () => {
  if (listenersAttached || !Capacitor.isNativePlatform()) {
    return;
  }
  listenersAttached = true;

  PushNotifications.addListener("registration", async (token: Token) => {
    storePushToken(token.value);
    await sendTokenUpdate(token.value, true);
  });

  PushNotifications.addListener("registrationError", (error) => {
    console.warn("Push registration error:", error);
  });

  PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
    console.debug("Push received:", notification);
  });

  PushNotifications.addListener("pushNotificationActionPerformed", (action: ActionPerformed) => {
    const data = action.notification?.data || {};
    const dataRecord = data as Record<string, unknown>;
    let nestedData: Record<string, unknown> | null = null;
    const rawNested = dataRecord.data;
    if (typeof rawNested === "string") {
      try {
        const parsed = JSON.parse(rawNested);
        if (parsed && typeof parsed === "object") {
          nestedData = parsed as Record<string, unknown>;
        }
      } catch {
        // Ignore parse failures
      }
    } else if (rawNested && typeof rawNested === "object") {
      nestedData = rawNested as Record<string, unknown>;
    }

    const pickDeepLink = (source: Record<string, unknown> | null | undefined) => {
      if (!source) return undefined;
      const pinpointData = typeof source.pinpoint === "object"
        ? source.pinpoint as Record<string, unknown>
        : {};
      const candidates = [
        source.deep_link,
        source.deeplink,
        source.url,
        source.Url,
        source.link,
        source["pinpoint.url"],
        source["pinpoint.deeplink"],
        pinpointData.url,
        pinpointData.deeplink,
      ];
      return candidates.find(value => typeof value === "string" && value.trim()) as string | undefined;
    };

    let deepLink = pickDeepLink(dataRecord) || pickDeepLink(nestedData);
    if (!deepLink) {
      const entityId = dataRecord.entity_id
        ?? dataRecord.entityId
        ?? dataRecord.target_id
        ?? dataRecord.targetId
        ?? nestedData?.entity_id
        ?? nestedData?.entityId
        ?? nestedData?.target_id
        ?? nestedData?.targetId;
      if (typeof entityId === "string" && entityId.trim()) {
        deepLink = `/stuff/${entityId.trim()}`;
      }
    }
    // Store notif_id so we can mark the inbox item as read after auth hydration.
    // Check push data first, then fall back to _nid query param in the deep link URL
    // (Pinpoint Data dict isn't reliably surfaced on iOS).
    let notifId = (dataRecord.notif_id ?? nestedData?.notif_id) as string | undefined;
    if (!notifId && typeof deepLink === "string") {
      const nidMatch = deepLink.match(/[?&]_nid=([^&]+)/);
      if (nidMatch) notifId = nidMatch[1];
    }
    if (typeof notifId === "string" && notifId.trim()) {
      setStoredValue(PENDING_PUSH_READ_KEY, notifId.trim());
    }

    console.debug("Push action performed:", action.notification?.data || action);
    if (typeof deepLink === "string" && deepLink.trim()) {
      // Strip _nid param before navigating — it's only for badge sync
      const cleanLink = deepLink.trim().replace(/([?&])_nid=[^&]+(&|$)/, (_, p, a) => a ? p : "").replace(/\?$/, "");
      dispatchDeepLink(cleanLink);
    }
  });
};

export const initializePushListeners = () => {
  attachListeners();
};

export const registerForPushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  attachListeners();
  const permissions = await PushNotifications.checkPermissions();
  if (permissions.receive !== "granted") {
    return;
  }
  await PushNotifications.register();
};

export const requestPushPermissionsAndRegister = async (): Promise<PermissionStatus | null> => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }
  attachListeners();
  const status = await PushNotifications.requestPermissions();
  if (status.receive === "granted") {
    await PushNotifications.register();
  }
  return status;
};

export const syncPushEnabledState = async (enabled: boolean) => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const token = loadPushToken();
    if (!token) {
      if (enabled) {
        await requestPushPermissionsAndRegister();
      }
      return;
    }
    await sendTokenUpdate(token, enabled);
  } catch (error) {
    console.warn("Failed to sync push enabled state:", error);
  }
};

/** Check whether iOS/Android has actually granted push notification permissions. */
export const checkPushPermissionGranted = async (): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await PushNotifications.checkPermissions();
    return result.receive === "granted";
  } catch {
    return false;
  }
};

/** Set the app icon badge to a specific count. */
export const setNotificationsBadgeCount = async (count: number) => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await StashtBadge.setBadgeCount({ count: Math.max(0, count) });
  } catch (error) {
    console.warn("Failed to set badge count:", error);
  }
};

/**
 * Clear the app icon badge and remove delivered notifications from the notification center.
 * Call this when the user views or marks notifications as read.
 */
export const clearNotificationsBadge = async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await StashtBadge.clear();
  } catch (error) {
    console.warn("Failed to clear badge:", error);
  }
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.warn("Failed to clear delivered notifications:", error);
  }
};

/** Remove delivered notifications from the notification center without touching the badge. */
export const clearDeliveredNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
  } catch (error) {
    console.warn("Failed to clear delivered notifications:", error);
  }
};

/**
 * Sync the app icon badge to the actual unread inbox count.
 * Accepts an optional pre-computed count to avoid an extra API fetch.
 */
export const syncBadge = async (knownUnreadCount?: number) => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    let unread: number;
    if (knownUnreadCount !== undefined) {
      unread = knownUnreadCount;
    } else {
      const inbox = await notificationsService.getInbox(50);
      unread = inbox.items.filter(i => !i.read).length;
    }
    if (unread === 0) {
      await StashtBadge.clear();
    } else {
      await StashtBadge.setBadgeCount({ count: unread });
    }
  } catch {
    // Best effort — don't block the caller
  }
};

/**
 * Process a pending push notification read (stored on push tap).
 * Marks the corresponding inbox item as read, then syncs badge.
 * Always syncs badge at the end, even if there's nothing pending.
 */
export const processPendingPushRead = async () => {
  const notifId = getStoredValue(PENDING_PUSH_READ_KEY);
  if (notifId) {
    try {
      const inbox = await notificationsService.getInbox(50);
      const item = inbox.items.find(i => i.notif_id === notifId);
      if (item && !item.read && item.inbox_ts) {
        await notificationsService.markInboxRead({ inbox_ts: [item.inbox_ts] });
      }
      clearStoredValue(PENDING_PUSH_READ_KEY);
    } catch {
      // Leave pending for retry on next app start
      return;
    }
  }
  await syncBadge();
};
